import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { callFunction } from "../firebase";
import { getPlanningDateString } from "../planningDates";
import { UserContext } from "../components/AuthGuard";

const noop = () => {};

export const PlanningRolloverContext = createContext({
  rolloverInfo: null,
  dismissRolloverInfo: noop,
});

export function usePlanningRollover() {
  return useContext(PlanningRolloverContext);
}

export function PlanningRolloverProvider({ children, fallback = null }) {
  const [user, setUser] = useContext(UserContext);
  const [rolloverInfo, setRolloverInfo] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const lastProcessedPlanningDayRef = useRef("");
  const inFlightRef = useRef(false);
  const userId = user?.uid || user?.id;
  const timezone = user?.timezone || "UTC";

  const runRollover = useCallback(async (force = false) => {
    if (!userId || inFlightRef.current) return;

    const planningDay = getPlanningDateString(timezone);
    if (!force && lastProcessedPlanningDayRef.current === planningDay) {
      return;
    }

    inFlightRef.current = true;

    try {
      const result = await callFunction("planning/process-overdue", {}, "POST");
      if (result?.error) {
        return;
      }

      lastProcessedPlanningDayRef.current = planningDay;

      if (typeof result?.newPoints === "number") {
        setUser((prev) => (
          prev
            ? { ...(prev || {}), points: result.newPoints }
            : prev
        ));
      }

      if ((result?.overdueItems || 0) > 0 || (result?.bonusPoints || 0) > 0) {
        setRolloverInfo(result);
      }
    } finally {
      inFlightRef.current = false;
      setInitializing(false);
    }
  }, [setUser, timezone, userId]);

  useEffect(() => {
    if (!userId) {
      setInitializing(false);
      setRolloverInfo(null);
      lastProcessedPlanningDayRef.current = "";
      return;
    }

    setInitializing(true);
    void runRollover(true);
  }, [runRollover, userId, timezone]);

  useEffect(() => {
    if (!userId) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void runRollover(false);
      }
    };

    const handleFocus = () => {
      void runRollover(false);
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [runRollover, userId]);

  const value = useMemo(() => ({
    rolloverInfo,
    dismissRolloverInfo: () => setRolloverInfo(null),
  }), [rolloverInfo]);

  if (initializing) {
    return fallback;
  }

  return (
    <PlanningRolloverContext.Provider value={value}>
      {children}
    </PlanningRolloverContext.Provider>
  );
}

PlanningRolloverProvider.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
};
